import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import { setLanguage } from "../redux/actions";

const LanguageSwitcher = ({ placement = "bottom", variant = "default" }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef(null);
  const currentLanguage = useSelector((state) => state.language.language);
  const dropUp = placement === "top";
  const panel = variant === "panel";
  const isHeader = variant === "header";

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    
    // Update Redux state
    const direction = lng === "ar" ? "rtl" : "ltr";
    dispatch(setLanguage(lng, direction));
    
    // Set document direction for RTL support
    document.body.dir = direction;
    document.documentElement.dir = direction;
    
    setOpen(false);
  };

  // Initialize language on component mount
  useEffect(() => {
    const storedLang = localStorage.getItem('i18nextLng') || localStorage.getItem('i18nextLang') || 'en';
    if (!currentLanguage && i18n.language) {
      const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
      dispatch(setLanguage(i18n.language, direction));
    }
  }, [currentLanguage, i18n.language, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const languages = [
    { code: 'en', label: 'English', shortLabel: 'EN' },
    { code: 'ar', label: 'العربية', shortLabel: 'AR' }
  ];

  const displayLanguage = currentLanguage || i18n.language || 'en';
  // Handle locale codes like 'en-US' -> 'en', 'ar-SA' -> 'ar'
  const languageCode = displayLanguage.split('-')[0];
  const currentLang = languages.find(lang => lang.code === languageCode);

  const buttonStyle = isHeader
    ? {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        background: isHovered ? "#f5f5f5" : "#ffffff",
        border: "1px solid #e8e8e8",
        cursor: "pointer",
        fontSize: "10px",
        color: "#000000",
        padding: "6px 12px",
        borderRadius: "10px",
        transition: "background-color 0.2s ease",
        fontFamily: "Inter, sans-serif",
        fontWeight: 500,
        letterSpacing: "0.25px",
        lineHeight: "19.6px",
        boxSizing: "border-box",
      }
    : {
        display: "flex",
        alignItems: "center",
        gap: panel ? "6px" : "4px",
        background: !panel && isHovered ? "#f5f5f5" : "transparent",
        border: "none",
        cursor: "pointer",
        fontSize: panel ? "14px" : "17px",
        fontWeight: panel ? 600 : 500,
        color: "inherit",
        padding: panel ? "0" : "8px 6px",
        borderRadius: "6px",
        transition: "background-color 0.2s ease",
        maxHeight: "36px",
      };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Button with Globe, current language (EN/AR), and arrow */}
      <button
        onClick={() => setOpen(!open)}
        style={buttonStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Icon icon="solar:global-linear" width={isHeader ? 14 : panel ? 16 : 17} />
        
          <span style={{ fontWeight: panel ? 600 : 500, lineHeight: "16px", maxHeight: '36px', display: 'flex', alignItems: 'center', fontSize: isHeader ? 10 : undefined }}>{currentLang?.shortLabel || (languageCode === 'ar' ? 'AR' : 'EN')}</span>
        
        <Icon 
          icon="solar:alt-arrow-down-linear" 
          width={panel ? 10 : 14}
          style={{
            transform: open
              ? dropUp
                ? "rotate(0deg)"
                : "rotate(180deg)"
              : dropUp
                ? "rotate(180deg)"
                : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: "absolute",
            ...(dropUp
              ? { bottom: "110%" }
              : { top: "110%" }),
            insetInlineEnd: 0,
            background: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            zIndex: 9999,
            overflow: "hidden",
            minWidth: "90px",
          }}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              style={{
                padding: "10px 16px",
                background: languageCode === lang.code ? "#f0f7ff" : "none",
                border: "none",
                width: "100%",
                textAlign: "left",
                cursor: "pointer",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: languageCode === lang.code ? "#1890ff" : "#333",
                transition: "background-color 0.2s ease",
              }}
              onClick={() => changeLanguage(lang.code)}
              onMouseEnter={(e) => {
                if (languageCode !== lang.code) {
                  e.currentTarget.style.backgroundColor = "#f5f5f5";
                }
              }}
              onMouseLeave={(e) => {
                if (languageCode !== lang.code) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              <span style={{ fontWeight: "500", maxHeight: '36px', display: 'flex', alignItems: 'center' }}>{lang.shortLabel}</span>
        
              {languageCode === lang.code && (
                <Icon 
                  icon="solar:check-circle-bold" 
                  width={16} 
                  style={{ marginInlineStart: "auto", color: "#1890ff" }}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;